import { ControlledSelect, ControlledTextField } from '@if/mui'
import { CardContent, CardHeader, CircularProgress, Grid, MenuItem, Stack, Typography } from '@mui/material'
import { CardBorderTop } from '@src/components/common/Card'
import { FinancingProgramProvider } from '@src/types/FinancingProgramProvider'
import { MechanicalWarrantyProduct } from '@src/types/MechanicalWarrantyProduct'
import { MechanicalWarrantyWorksheet } from '@src/types/WorksheetSchema'
import { useEffect, useMemo, useRef } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import CheckboxGroupField from '../../components/CheckboxGroupField'

interface Props {
  merchantMechanicalWarrantyProviders: FinancingProgramProvider[]
  merchantMechanicalWarrantyProducts: MechanicalWarrantyProduct[]
  isLoadingProducts?: boolean
}

const MechanicalWarrantyExtended = ({ merchantMechanicalWarrantyProviders, merchantMechanicalWarrantyProducts, isLoadingProducts = false }: Props) => {
  const { control, setValue, getValues } = useFormContext<MechanicalWarrantyWorksheet>()
  const { t, i18n } = useTranslation()

  const selectedProviderId = useWatch({ control, name: 'providerId' })
  const selectedProductId = useWatch({ control, name: 'mechanicalWarrantyProductId' })
  const prevProviderId = useRef(selectedProviderId)

  useEffect(() => {
    if (prevProviderId.current && prevProviderId.current !== selectedProviderId && selectedProviderId) {
      setValue('mechanicalWarrantyProductId', '', { shouldDirty: true })
      setValue('additions', { options: [], surcharges: [] }, { shouldDirty: true })
    }
    prevProviderId.current = selectedProviderId
  }, [selectedProviderId, setValue])

  useEffect(() => {
    if (!isLoadingProducts && merchantMechanicalWarrantyProducts?.length > 0) {
      const currentProductId = getValues('mechanicalWarrantyProductId')

      if (!currentProductId) {
        setValue('mechanicalWarrantyProductId', merchantMechanicalWarrantyProducts[0].id, {
          shouldDirty: true,
        })
      }
    }
  }, [merchantMechanicalWarrantyProducts, setValue, getValues, isLoadingProducts])

  const selectedProduct = useMemo(() => {
    return merchantMechanicalWarrantyProducts?.find((p) => p.id === selectedProductId)
  }, [merchantMechanicalWarrantyProducts, selectedProductId])

  const surchargeOptions = useMemo(() => {
    return (selectedProduct?.surcharges ?? []).map((s) => ({
      label: i18n.language === 'fr' ? s.nameFr : s.nameEn,
      value: s.id,
    }))
  }, [selectedProduct, i18n.language])

  const optionOptions = useMemo(() => {
    return (selectedProduct?.options ?? []).map((o) => ({
      label: i18n.language === 'fr' ? o.nameFr : o.nameEn,
      value: o.id,
    }))
  }, [selectedProduct, i18n.language])

  return (
    <CardBorderTop>
      <CardHeader title={t('mechanicalWarrantyWorksheet.extendedWarranty')} />
      <CardContent component={Stack} spacing={2}>
        <Grid sx={{ mb: 3 }}>
          <ControlledSelect
            label={t('mechanicalWarrantyWorksheet.providerLabel')}
            control={control}
            name="providerId"
            fullWidth
            disableEmptyOption
          >
            {merchantMechanicalWarrantyProviders?.map((provider) => (
              <MenuItem key={provider.id} value={provider.id}>
                {i18n.language === 'fr' ? provider.nameFr : provider.nameEn}
              </MenuItem>
            ))}
          </ControlledSelect>
        </Grid>

        <Grid sx={{ mb: 3 }}>
          {isLoadingProducts ? (
              <Stack direction="row" justifyContent="left" alignItems="left" sx={{ minHeight: 40 }}>
                <CircularProgress size={24} />
              </Stack>
          ) : (
            <ControlledSelect
              label={t('mechanicalWarrantyWorksheet.productLabel')}
              control={control}
              name="mechanicalWarrantyProductId"
              fullWidth
              disableEmptyOption
            >
              {merchantMechanicalWarrantyProducts?.map((product) => (
                <MenuItem key={product.id} value={product.id}>
                  {i18n.language === 'fr' ? product.nameFr : product.nameEn}
                </MenuItem>
              ))}
            </ControlledSelect>
          )}
        </Grid>
        <Stack>
          {surchargeOptions.length > 0 && (
            <>
              <Typography variant="subtitle2">{t('mechanicalWarrantyWorksheet.surcharge')}</Typography>
              <CheckboxGroupField
                name="additions.surcharges"
                options={surchargeOptions}
              />
            </>
          )}
          {optionOptions.length > 0 && (
            <>
              <Typography variant="subtitle2">{t('mechanicalWarrantyWorksheet.option')}</Typography>
              <CheckboxGroupField
                name="additions.options"
                options={optionOptions}
              />
            </>
          )}
        </Stack>
        <ControlledTextField
          label={t('mechanicalWarrantyWorksheet.policy')}
          control={control}
          name="policy"
          required
        />
      </CardContent>
    </CardBorderTop>
  )
}

export default MechanicalWarrantyExtended
